import type { WordsType } from './i18n/words.js';

/**
 * PX-25 (`docs/concept-design-agency-production-and-automation.md` §24.5, walkthrough A step 2): when a
 * customer sends a request, the work area's agent drafts the reply — what it understood, and the
 * questions it needs answered, each with a suggested answer — from the request and the earlier work
 * shared with that customer. The draft is the agent's comment to the customer, so PX-16's gate holds it
 * until a person sends it.
 *
 * Which agent drafts is a work-area setting, `policies.requestReplyDraft` as `{ agentId }`; nothing
 * stored, no draft is made. The model is the work area's own AI settings (ADR-126): switched off, no
 * call and no draft.
 */
export const REQUEST_REPLY_DRAFT_POLICY_KEY = 'requestReplyDraft';

/** The most questions a draft asks: a reply, not a questionnaire. */
export const REQUEST_REPLY_DRAFT_MAX_QUESTIONS = 5;
/** The longest a draft's "understood" sentence and each question or answer are kept. */
export const REQUEST_REPLY_DRAFT_TEXT_MAX = 600;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The agent that drafts replies to requests here, or null for none (the default). */
export function requestReplyDraftAgentOf(policies: unknown): string | null {
  if (!policies || typeof policies !== 'object' || Array.isArray(policies)) return null;
  const section = (policies as Record<string, unknown>)[REQUEST_REPLY_DRAFT_POLICY_KEY];
  return requestReplyDraftPolicyProblem(section) === null &&
    section &&
    typeof (section as { agentId?: unknown }).agentId === 'string'
    ? (section as { agentId: string }).agentId
    : null;
}

/** The merge patch that stores the setting; null switches drafting off. */
export function requestReplyDraftPolicyPatch(agentId: string | null): Record<string, unknown> {
  return { [REQUEST_REPLY_DRAFT_POLICY_KEY]: agentId ? { agentId } : null };
}

/**
 * What is wrong with the `requestReplyDraft` section of a `policies` write, or null: the words that
 * follow `policies.requestReplyDraft` in the server's refusal. It names one agent by its id and holds
 * nothing else.
 */
export function requestReplyDraftPolicyProblem(section: unknown): string | null {
  if (section === undefined || section === null) return null;
  if (typeof section !== 'object' || Array.isArray(section)) {
    return `must be an object naming the drafting agent as agentId — ${JSON.stringify(section)} is not.`;
  }
  const value = section as Record<string, unknown>;
  const extra = Object.keys(value).filter((key) => key !== 'agentId');
  if (extra.length > 0) return `holds only agentId — not ${extra.join(', ')}.`;
  if (typeof value.agentId !== 'string' || !UUID.test(value.agentId)) {
    return `must name the drafting agent by its id as agentId — ${JSON.stringify(value.agentId)} is not one.`;
  }
  return null;
}

/**
 * F-0786 PAG-7a: the code of the 400 that refuses a `policies` write naming, as the drafting agent,
 * an agent FEATit does not run (or none). The reply is drafted in FEATit (PAG-7), so the agent has to
 * be a platform agent; the screen words the refusal from this code (`failures.replyDraftAgentNotPlatform`).
 */
export const REQUEST_REPLY_DRAFT_AGENT_NOT_PLATFORM = 'request_reply_draft_agent_not_platform';

/** The server's sentence for {@link REQUEST_REPLY_DRAFT_AGENT_NOT_PLATFORM}, which the CLI prints as it is. */
export function requestReplyDraftAgentNotPlatformMessage(field: string, agentId: string): string {
  return (
    `${field}.${REQUEST_REPLY_DRAFT_POLICY_KEY}.agentId names ${agentId}, which is not an agent FEATit runs: ` +
    'the reply is drafted in FEATit, so the drafting agent has to be a platform agent. Make one with ' +
    '`featit agent-new <key> --name <name> --runtime platform` and name it. Nothing was saved.'
  );
}

/** What the model is asked to return, read and bounded. */
export interface IRequestReplyDraft {
  understood: string;
  questions: Array<{ question: string; suggestedAnswer: string }>;
}

/** What the drafting agent is told: the shape of its answer and the customer's words. */
export const REQUEST_REPLY_DRAFT_SYSTEM_PROMPT =
  'You draft the first reply an agency sends a customer who has just sent a request. A person at the ' +
  'agency reads, edits and sends your draft; you never send it. Answer with one JSON object and ' +
  'nothing else: {"understood": "<one or two sentences: what you understood the customer wants>", ' +
  '"questions": [{"question": "<one question you need answered>", "suggestedAnswer": "<the answer ' +
  'you would suggest, so the customer can just agree>"}]}. Ask at most ' +
  `${REQUEST_REPLY_DRAFT_MAX_QUESTIONS} questions, only ones the request leaves open. Write in the ` +
  'language the request is written in, address the customer informally (in German: du), say "work", ' +
  'never "project", and name no internal key, status or role.';

function bounded(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim().slice(0, REQUEST_REPLY_DRAFT_TEXT_MAX);
  return text || null;
}

/**
 * The model's answer read as a draft, or null when it is not one: JSON (a fenced block is unwrapped)
 * with a non-empty "understood" and questions that each have both parts. Extra questions are dropped.
 */
export function parseRequestReplyDraft(text: string): IRequestReplyDraft | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(
      text
        .replace(/```json\s*/g, '')
        .replace(/```\s*/g, '')
        .trim(),
    );
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const value = parsed as { understood?: unknown; questions?: unknown };
  const understood = bounded(value.understood);
  if (!understood) return null;
  const questions = (Array.isArray(value.questions) ? value.questions : [])
    .map((one) => {
      const entry = (one ?? {}) as { question?: unknown; suggestedAnswer?: unknown };
      const question = bounded(entry.question);
      const suggestedAnswer = bounded(entry.suggestedAnswer);
      return question && suggestedAnswer ? { question, suggestedAnswer } : null;
    })
    .filter((one): one is { question: string; suggestedAnswer: string } => one !== null)
    .slice(0, REQUEST_REPLY_DRAFT_MAX_QUESTIONS);
  return { understood, questions };
}

/**
 * The draft as the customer will read it once a person sends it: a greeting by first name, what was
 * understood, and each question with its suggested answer, in the customer's language.
 */
export function requestReplyDraftText(
  words: WordsType,
  draft: IRequestReplyDraft,
  customerName: string | null | undefined,
): string {
  const first = customerName?.trim().split(/\s+/)[0];
  const greeting = first
    ? words('notifications.requestReplyDraft.greetingNamed', { name: first })
    : words('notifications.requestReplyDraft.greeting');
  const lines = [greeting, '', draft.understood];
  if (draft.questions.length > 0) {
    lines.push('', words('notifications.requestReplyDraft.questionsIntro'));
    draft.questions.forEach((one, index) => {
      lines.push(
        `${index + 1}. ${one.question}`,
        `   ${words('notifications.requestReplyDraft.suggested', { answer: one.suggestedAnswer })}`,
      );
    });
  }
  return lines.join('\n');
}

/**
 * Where a request's reply draft stands (PX-25), as the team reads it on the request: the agent is
 * drafting, it drafted (the draft is in the thread), it failed and why, or no model is set. `none`
 * when nothing was asked for (no agent named). Never shown to the customer.
 */
export const REQUEST_REPLY_DRAFT_STATES = {
  NONE: 'none',
  DRAFTING: 'drafting',
  DRAFTED: 'drafted',
  FAILED: 'failed',
  NO_MODEL: 'no-model',
} as const;
export type RequestReplyDraftStateType =
  (typeof REQUEST_REPLY_DRAFT_STATES)[keyof typeof REQUEST_REPLY_DRAFT_STATES];

/** Why a draft failed, in FEATit's words, never the model's. */
export const REQUEST_REPLY_DRAFT_FAILURES = {
  TIMEOUT: 'timeout',
  KEY_REFUSED: 'key-refused',
  UNREACHABLE: 'unreachable',
  RATE_LIMITED: 'rate-limited',
  MODEL_ERROR: 'model-error',
  UNREADABLE: 'unreadable',
  OTHER: 'other',
} as const;
export type RequestReplyDraftFailureType =
  (typeof REQUEST_REPLY_DRAFT_FAILURES)[keyof typeof REQUEST_REPLY_DRAFT_FAILURES];

/** `GET /work-items/:id/reply-draft`. */
export interface IRequestReplyDraftState {
  state: RequestReplyDraftStateType;
  failure: RequestReplyDraftFailureType | null;
  startedAt: string | null;
  finishedAt: string | null;
}

/**
 * A draft still "drafting" this long after it began is read as timed out: the server that asked
 * stopped before it answered (a restart), and nothing else will finish it. Well past the model's own
 * time limit (`FEATIT_AI_REQUEST_TIMEOUT_MS`, two minutes by default).
 */
export const REQUEST_REPLY_DRAFT_STALE_MS = 10 * 60_000;

/** The state as read at `now`: a drafting row past `REQUEST_REPLY_DRAFT_STALE_MS` has failed. */
export function requestReplyDraftStateAt(
  row: {
    state: string;
    failure: string | null;
    startedAt: string | Date | null;
    finishedAt: string | Date | null;
  } | null,
  now: Date,
): IRequestReplyDraftState {
  const iso = (value: string | Date | null) =>
    value === null ? null : new Date(value).toISOString();
  if (!row) return { state: 'none', failure: null, startedAt: null, finishedAt: null };
  const state = (Object.values(REQUEST_REPLY_DRAFT_STATES) as string[]).includes(row.state)
    ? (row.state as RequestReplyDraftStateType)
    : 'none';
  const failure = (Object.values(REQUEST_REPLY_DRAFT_FAILURES) as string[]).includes(
    row.failure ?? '',
  )
    ? (row.failure as RequestReplyDraftFailureType)
    : null;
  const startedAt = iso(row.startedAt);
  if (
    state === 'drafting' &&
    startedAt !== null &&
    now.getTime() - new Date(startedAt).getTime() > REQUEST_REPLY_DRAFT_STALE_MS
  ) {
    return { state: 'failed', failure: 'timeout', startedAt, finishedAt: null };
  }
  return {
    state,
    failure: state === 'failed' ? (failure ?? 'other') : null,
    startedAt,
    finishedAt: iso(row.finishedAt),
  };
}
